import { ref, computed, type ComputedRef, type Ref } from 'vue';
import TravelRuleClient from '@/network/travel-rule';
import type { Option, Vasp } from '@/types/travel-rule-wizard';

const OTHER_OPTION: Option = {
  value: 'Other',
  localizedTitle: 'transaction-flow.travel-rule.shared.option.other',
  // Stays reachable even when a search filters out every VASP match.
  pinned: true,
  id: 'other',
};

// Module-level state so both flows (send/buy & receive/sell) and re-opens of
// the popup share a single fetch instead of requesting the list each time.
const vasps = ref<Vasp[]>([]);
const isLoading = ref(false);
const error = ref<unknown>(null);
let inflight: Promise<void> | null = null;

interface UseTravelRuleVasps {
  vaspOptions: ComputedRef<Option[]>;
  isLoading: Ref<boolean>;
  error: Ref<unknown>;
  loadVasps: () => Promise<void>;
}

export const useTravelRuleVasps = (): UseTravelRuleVasps => {
  const loadVasps = (): Promise<void> => {
    if (inflight) {
      return inflight;
    }

    isLoading.value = true;
    error.value = null;

    inflight = TravelRuleClient.getTravelRuleVasps()
      .then(list => {
        vasps.value = list;
      })
      .catch((e: unknown) => {
        error.value = e;
      })
      .finally(() => {
        isLoading.value = false;
      });

    return inflight;
  };

  // While the list is loading, expose no options so the dropdown shows a loading
  // state rather than a lone "Other" entry. Once loaded, submit the VASP name as the
  // value (the id is ignored for now) and always append "Other" so the conditional
  // free-text platform field stays reachable.
  const vaspOptions = computed<Option[]>(() => {
    if (isLoading.value) {
      return [];
    }

    return [
      ...vasps.value.map(vasp => ({ value: vasp.name, label: vasp.name, id: vasp.id })),
      OTHER_OPTION,
    ];
  });

  return { vaspOptions, isLoading, error, loadVasps };
};
